/**
 *
 * Initial port of tracking contained in `init_page`
 *
 * @author Seraphina Orsini <sorsini@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
import visibility from './visibility';
import {queuePostLoadTask} from '@wayfair/scheduler';
import isPrivateBrowsing from './scribe_detect_private_browsing';
import isAdBlockerEnabled from './scribe_detect_ad_block';
import subscribe, {
  supportWebPushSubscription,
} from './scribe_web_push_subscription_tracker';

const SUBSCRIPTION_REQUEST = 'request_prompt';

const createTrackPageView = (client, options) => {
  return ({isAdBlockerEnabled, isPrivateBrowsing}) =>
    client.trackPageViewing({
      isAdBlockerEnabled,
      isPrivateBrowsing,
      ...options,
    });
};

const initPageVisiblityTracking = client => {
  const trackPageView = createTrackPageView(client, {
    transactionID: client.config.transactionID,
    isSPA: client.config.isSPA,
    marketingCategory: client.config.marketingCategory,
    pageType: client.config.pageType,
    b2bLevelName: client.config.b2bLevelName,
    datacenter: client.config.datacenter,
    serviceID: client.config.serviceID,
    // passing null for these values which are from device fingerprinting
    // which was not included in scope of port to decoupled lib
    canvasHashes: null,
    fontHash: null,
    pluginHash: null,
    languageHash: null,
  });

  queuePostLoadTask(() => {
    // Ad/track blockers are currently blocking our beacon endpoint
    // submitting a request to our beacon endpoint to determine if we have access and are not being blocked
    client.determineBeaconAccessibilty(); // PageViews should fire as soon as page comes into visibility
    visibility.onVisible(() => {
      isPrivateBrowsing().then(isPrivateBrowsing => {
        return isAdBlockerEnabled().then(isAdBlockerEnabled => {
          client.config.cookieApi.setCookie(
            'isAdBlockerEnabled',
            isAdBlockerEnabled,
            {expires: 0}
          );
          trackPageView({isAdBlockerEnabled, isPrivateBrowsing});
          client.trackVisibilityChanges();
        });
      });
    });

    visibility.afterPrerendering(() => {
      client.initializeHeartbeat();
    });

    client.trackBodyClicks();
  });
};

const initPageUnloadTracking = client => {
  window.addEventListener(
    'beforeunload',
    client.createSetLastRequestTxidCookie(client.config.transactionID),
    null
  );
};

const initLegacyClickTracking = (client, setClickLocation) => {
  if (typeof window !== 'undefined' && window.document.addEventListener) {
    window.document.addEventListener('click', ({target}) => {
      const clickLocation = target.getAttribute('data-click-location');
      // if we don't have a click location attribute,
      // or if we do but this is opening a new tab - do nothing
      if (!clickLocation || target === '_blank') {
        return;
      }

      const clickLocationMetadata = target.getAttribute(
        'click-location-metadata'
      );
      setClickLocation(clickLocation, clickLocationMetadata);
    });

    window.document.addEventListener('change', ({target}) => {
      const selectLocation = target.getAttribute('data-select-location');
      if (!selectLocation) {
        return;
      }
      const clickLocation = `${selectLocation}:${target.value}`;
      const clickLocationMetadata = target.getAttribute(
        'select-location-metadata'
      );
      setClickLocation(clickLocation, clickLocationMetadata);
    });

    window.document.addEventListener('click', ({target}) => {
      if (target.classList.contains('js-track-event')) {
        const eventName = target.getAttribute('data-event-name');
        const cstmVars = target.getAttribute('data-event-cstmvars');
        client.recordEvent(eventName, cstmVars);
      }
    });
  }
};

// sorsini added arg for should send push request
// basket and checkout should pass this as false, as well as
// any page where canSendPushNotificationRequest would be false currently
const initWebPushSubscriptionTracking = ({
  client,
  applicationServerKey,
  shouldSendWebPushRequest = true,
  logger,
}) => {
  // If we can send a push notification subscription request and the user is not checking out, schedule a postLoad
  // task to display the subscription request
  if (
    // wf.appData.canSendPushNotificationRequest &&
    // wf.appData.pageAlias !== 'Basket' &&
    // wf.appData.pageAlias !== 'Checkout'
    shouldSendWebPushRequest
  ) {
    // sorsini removed wf scheduler and lazy loading here for now, will need to implement again
    // this fn checks window/navigator/serviceworker all existing
    if (supportWebPushSubscription(applicationServerKey)) {
      client.recordEvent(SUBSCRIPTION_REQUEST, {});
      // NOTE: This is a dependency on the monolith service worker
      navigator.serviceWorker
        .register('/service_worker.js')
        .then(subscribe(applicationServerKey, client, logger));
    }
  }
};

const initPageTracking = ({
  client,
  applicationServerKey,
  setClickLocation,
  logger,
  shouldSendWebPushRequest = true,
}) => {
  // there's a bunch of window/nav uses throughout this, so just being extra safe
  if (typeof window !== 'undefined') {
    initLegacyClickTracking(client, setClickLocation);
    initPageUnloadTracking(client);
    initPageVisiblityTracking(client);
    initWebPushSubscriptionTracking({
      client,
      applicationServerKey,
      shouldSendWebPushRequest,
      logger,
    });
  }
};

export default initPageTracking;
